import {
    Alert,
    Box, Checkbox, Chip,
    FormControl,
    Grid, Paper,
    TextField,
    Typography
} from "@mui/material";
import GenericDialog from "../../../components/dialog/generic-dialog.component";
import * as React from "react";
import {MountJiga, MountJigaGroup, originOptions, ReuseFtbMaterial} from "../../../models/basic/ft-bom";
import {Field, Form, Formik} from "formik";
import TablePaginationComponent from "../../../components/table/table-pagination.component";
import TableActionComponent from "../../../components/table/table-action.component";
import {useEffect, useState} from "react";
import {ErrorComponent} from "../../../components/error/error.component";
import {FtBomService} from "../../../services/ft-bom-service";
import {toast} from "react-toastify";
import {translate} from "../../../translate/translate";

interface reuseMaterialProps {
    openDialog: boolean;
    onClose: (save?: boolean) => void;
    mountJiga?: MountJiga | MountJigaGroup | undefined;
    visualization?: boolean;
	idClient: string;
    totalGroup?: number;
}

const ReuseFtbMaterialComponent: React.FC<reuseMaterialProps> = ({
    openDialog,
    onClose,
    mountJiga,
    visualization = false,
	idClient,
    totalGroup
}) => {
    const service = new FtBomService();
    const [loading, setLoading] = React.useState<boolean>(false);
    const [isDisabled, setIsDisabled] = React.useState<boolean>(true);

    const [ftBoms, setFtBoms] = React.useState<ReuseFtbMaterial[]>([]);
    const [quantitySented, setQuantitySent] = useState<{[key: number]: number}>();
    const [auxQuantitySented, setAuxQuantitySented] = useState<{[key: number]: number}>({});

    const [totalReused, setTotalReused] = React.useState<number>(0);
    const [totalStock, setTotalStock] = React.useState<number>(0);

    useEffect(() => {
        if (openDialog) {
            clearValues();
            loadList();
            if (mountJiga) {
                setTotalReused((mountJiga as MountJiga)?.qty_reused || 0);
                setTotalStock((mountJiga as MountJiga)?.total_jiga - ((mountJiga as MountJiga)?.qty_reused || 0));
            }
        }
    }, [openDialog]);

    const loadList = () => {
        if (visualization) {
            service.load_reused_ftboms(idClient, (mountJiga as MountJigaGroup).ft_bom, (mountJiga as MountJigaGroup).alternative_group_bom).then(response => {
                setFtBoms(response.data);
            }).catch((error: any) => {
                ErrorComponent(error);
            });
            return;
        }
		service.load_ftboms_to_reuse((mountJiga as MountJiga)!.id).then(response => {
			setFtBoms(response.data);
            const qtySent = response.data.reduce((acc: {[key: number]: number}, item: ReuseFtbMaterial) => {
                acc[item.id] = item.total_used || 0;
                return acc;
            }, {})
			setQuantitySent(qtySent);
            setAuxQuantitySented(qtySent);
		}).catch((error: any) => {
			ErrorComponent(error);
		});
    };

    const handleSubmit = () => {
        if (mountJiga) {
            const ft_boms = ftBoms.map((it: ReuseFtbMaterial) => {
                return {
                    id: it.id,
                    ft_bom: it.ft_bom,
                    total_available: it.total_available,
                    total_reuse: quantitySented ? quantitySented[it.id] : 0,
                    origin: it.origin,
                    total_material: it.total_material || 0,
                };
            });

            service.save_reuse((mountJiga as MountJiga).id, ft_boms)
                .then((response: any) => {
                    toast.success(translate.t("successfully_updated"));
                    onClose(true);
                    clearValues();
                })
                .catch((error: any) => {
                    ErrorComponent(error);
                })
        }
    }

    useEffect(() => {

        if (quantitySented) {
            const listSelected = Object.values(quantitySented);
            const hasInvalid = ftBoms.some((it: ReuseFtbMaterial) => {
                return it.total_material < (quantitySented ? quantitySented[it.id] : 0)
                    || ((it.total_available + (it.total_used || 0)) < (quantitySented ? quantitySented[it.id] : 0));
            });
            const sum = listSelected.reduce((acc, curr) => acc + curr, 0) || 0;

            const auxListSelected = Object.values(auxQuantitySented);
            const allEmpty = auxListSelected.length > 0 && auxListSelected.every(value => value === 0) && listSelected.every(value => value === 0);

            setIsDisabled(
                !listSelected.length || hasInvalid ||
                sum > (mountJiga as MountJiga)?.total_jiga! ||
				(mountJiga as MountJiga)?.total_jiga! === 0 ||
                allEmpty
            );
            setTotalReused(sum);
            let totalS = ((mountJiga as MountJiga)?.total_jiga || 0) - sum
            setTotalStock(totalS < 0 ? 0 : totalS);
        } else {
            setTotalReused((mountJiga as MountJiga)?.qty_reused || 0);
            setTotalStock((mountJiga as MountJiga) ? (mountJiga as MountJiga)?.total_jiga - ((mountJiga as MountJiga)?.qty_reused || 0) : 0);
            setIsDisabled(true);
        }

    }, [quantitySented]);

    const clearValues = () => {
        setFtBoms([]);
        setQuantitySent({});
        setIsDisabled(true);
    }

    const calcularTotal = (mapa: { [key: string]: number }) => {
        return Object.values(mapa).reduce((accumulator: number, currentValue: number) => {
            return accumulator + currentValue;
        }, 0);
    }


    const customCellRender = (column: string, index: number, row: ReuseFtbMaterial) => {
        if (column === "origin") {
            const origin = originOptions.find(option => option.id === row.origin);
            return origin ? (
                <Chip size="small"
                      color={origin!.color as any}
                      label={origin!.label}
                />
            ) : ("");
        }
        if (column === "ft_bom") {
            return <Typography variant="body2" sx={{fontWeight: "bold"}}>
                {row.ft_bom}
                <Chip size="small"
                      color="secondary"
                      sx={{marginLeft: "8px"}}
                      label={"Total materiais: " + (row.total_material? row.total_material : 0)}
                />
                <Chip size="small"
                      color={row.total_available !== 0 ? 'success' : 'error' }
                      sx={{marginLeft: "8px"}}
                      label={"Total disponível: " + row.total_available}
                />
            </Typography>
        }
        if (column === "quantity") {
            return (
                <Formik
                    enableReinitialize={true}
                    validateOnMount={true}
                    initialValues={{
                        quantity_sent: 0,
                    }}
                    onSubmit={(values, {setSubmitting}) => {}}
                >
                    {
                        () => (
                            <Form autoComplete="off">
                                <Grid alignItems="center" container spacing={2} direction={"row"}>
                                    <Grid  sx={{textAlign: "right"}}>
                                        Total reutilização:
                                    </Grid>
                                    <Grid >
                                        <TextField
                                            name="quantity_sent_rma"
                                            variant="outlined"
                                            fullWidth
                                            type="number"
                                            value={quantitySented?.[row.id] ?? 0}
                                            error={quantitySented && (quantitySented[row.id] > 0 &&
                                                (       quantitySented[row.id] > row.total_material
                                                    ||  quantitySented[row.id] > (mountJiga as MountJiga)?.total_jiga!
                                                    ||  quantitySented[row.id] > row.total_available + (row.total_used || 0)
                                                    ||  ((mountJiga as MountJiga)?.quantity_origin_jiga || 0  ) + calcularTotal(quantitySented) > (mountJiga as MountJiga)?.total_jiga!)
                                                )}
                                            onChange={(event) => {
                                                const parsed = parseInt(event.target.value, 10) || 0;
                                                if (parsed < 0 || isNaN(parsed) || event.target.value.length > 10) return;
                                                setQuantitySent((prev) => ({
                                                    ...prev,
                                                    [row.id]: parsed
                                                }));
                                            }}
                                            onBlur={(event) => {
                                                const quantity = quantitySented ? quantitySented[row.id] : 0;
                                                setQuantitySent((prev) => ({
                                                    ...prev,
                                                    [row.id]: quantity
                                                }));
                                            }}
                                            inputProps={{
                                                sx: {
                                                    maxHeight: '0px !important',
                                                    height: '0px !important',
                                                },
                                                max: row.total_material
                                            }}
                                            disabled={!row.total_material}
                                        />
                                    </Grid>
                                </Grid>
                            </Form>
                        )}
                </Formik>
            );
        }
        return "-";
    };

    return (
        <GenericDialog
            open={openDialog}
            onClose={onClose}
            title={visualization ? "reused_ftb_material" : "reuse_ftb_material"}
            colorButton={"primary"}
            labelButton={"reuse"}
            disableConfirm={isDisabled || loading}
            loading={loading}
            closeButtonText="close"
            maxWidth="md"
            hideConfirmButton={visualization}
            handleConfirm={handleSubmit}>
            <Box>
                {!visualization && (
                    <Box sx={{marginTop: "10px", padding: "5px", backgroundColor: "#f2f2f2", borderRadius: "8px"}}
                         display="flex" flexDirection="column" gap={2}>
	                    <Typography>
		                    <span style={{ fontWeight: "bold" }}>Material:</span> {(mountJiga as MountJiga)?.foxconn_part_number}
	                    </Typography>
	                    <Typography>
		                    <span style={{ fontWeight: "bold" }}>Descrição EN:</span> {(mountJiga as MountJiga)?.description_english}
	                    </Typography>
	                    <Typography>
		                    <span style={{ fontWeight: "bold" }}>Descrição PT:</span> {(mountJiga as MountJiga)?.description_portuguese}
	                    </Typography>
	                    <Typography>
		                    <span style={{ fontWeight: "bold" }}>Quantidade pendente no grupo:</span> {((mountJiga as MountJiga)?.total_jiga ?? 0) - (((mountJiga as MountJiga)?.qty_reused ?? 0) + (totalGroup ?? 0))}
	                    </Typography>
                    </Box>
                )}

                {!visualization && (
                    <Box display="flex" flexDirection="column" gap={2} sx={{marginTop: "16px"}}>
                        <Typography>
                            Adicione a quantidade de material que será reutilizado<span style={{color: "red"}}>*</span>:
                        </Typography>
                        <Typography>
                            Total a reutilizar: <strong>{totalReused}</strong>
                        </Typography>
                    </Box>
                )}

                {visualization && (<Typography sx={{marginTop: "20px", fontWeight: "bold"}}>
                    Material(is) reutilizado(s) das FTBs:
                </Typography>)}


                {!visualization && (<Paper variant="outlined" sx={{marginTop: "16px"}}>
                    <TablePaginationComponent
                        state={ftBoms}
                        hideHeader={true}
                        displayedColumnsShow={["FTB", "Total"]}
                        displayedColumns={["ft_bom", "quantity"]}
                        actionsComponent={TableActionComponent}
                        loading={loading}
                        customColumns={["ft_bom", "quantity"]}
                        customCellRender={customCellRender}
                        columnStyles={{1: "70%", 2: "30%"}}
                    />
                </Paper>)}

                {visualization && (<Paper variant="outlined" sx={{marginTop: "10px"}}>
                    <TablePaginationComponent
                        state={ftBoms}
                        displayedColumnsShow={["Material Code", "Description", "Qty", "FTB"]}
                        displayedColumns={["material_code", "description_english", "total_reuse", "ft_bom"]}
                        actionsComponent={TableActionComponent}
                        loading={loading}
                        customColumns={["origin"]}
                        customCellRender={customCellRender}
                    />
                </Paper>)}

            </Box>
        </GenericDialog>
    )
}

export default ReuseFtbMaterialComponent;
